// Basic
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import useSessionStore from '@/app/store/session';
import actionsOrganizations from '../../api/actionsOrganizations';
// Components
import SimOffersSection from '../simOffers/SimOffersSection';
import { Skeleton } from '@/common-utils/components/ui/skeleton';
import ModalUpdateOrganizationSimOffers from '../modals/simOffers/ModalUpdateOrganizationSimOffers';
import { Separator } from '@/common-utils/components/ui/separator';
import { Button } from '@/common-utils/components/ui/button';
// Common
import SIM_OFFERS_PROVIDERS from '../../../sims/enums/SimOffersProviders';
import ICONS from '@/common-utils/enums/icons';
import hasRequiredPermissions from '@/common-utils/functions/hasRequiredPermissions';
import Permissions from '@/app/permissions/Permissions';
// Lang
import { nsOrganization } from '@/app/i18n/namespaces';

/**
 * @param {string} organizationId
 * @returns {JSX} SimOffersPanel
 */
function SimOffersPanel({ organizationId }) {
    // Constants
    const simOffersCategories = Object.values(SIM_OFFERS_PROVIDERS);

    // State
    const [launchRefetch, setLaunchRefetch] = useState(true);
    const [showModalUpdateOranizationSimOffers, setShowModalUpdateOranizationSimOffers] = useState(false);

    // Store
    const { permissions: userPermissions } = useSessionStore(s => s.session);

    // Api calls
    const { data, isFetching, refetch } = actionsOrganizations.getOrganizationSimOffers({ organization: organizationId }, true);

    // Hooks
    useEffect(() => {
        if (organizationId && launchRefetch) {
            refetch();
            setLaunchRefetch(false);
        }
    }, [launchRefetch, organizationId, refetch]);

    return (
        <div>
            <div>
                <Button
                    onClick={() => setShowModalUpdateOranizationSimOffers(true)}
                    icon={ICONS.EDIT}
                    variant='primary'
                    entity='actions.page.update_sim_offers'
                    namespace={nsOrganization}
                    disabled={!hasRequiredPermissions({
                        permissions: userPermissions,
                        requiredPermissions: [Permissions.CHANGE_ORGANIZATION_SIM_OFFER.name, Permissions.GET_ORGANIZATION_SIM_OFFERS.name]
                    })}
                />
            </div>
            <div>
                <div className='pt-4'>
                    {isFetching &&
                        <div className='space-y-6'>
                            <div>
                                <Skeleton className='h-6 w-64' />
                            </div>
                            <div className='flex flex-wrap gap-8'>
                                {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
                                    <Skeleton key={item} className='h-6 w-32' />
                                ))}
                            </div>
                            <div>
                                <Skeleton className='h-6 w-64' />
                            </div>
                            <div className='flex flex-wrap gap-8'>
                                {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
                                    <Skeleton key={item} className='h-6 w-32' />
                                ))}
                            </div>
                        </div>
                    }
                    {!isFetching && data &&
                        <>
                            {simOffersCategories.map((simOffersCategory, index) =>
                                <div key={simOffersCategory.key} >
                                    <SimOffersSection
                                        organizationSimOffers={data}
                                        simOffersCategory={simOffersCategory}
                                    />
                                    {index !== simOffersCategories.length - 1 && <Separator className='my-4' />}
                                </div>
                            )}
                        </>
                    }
                </div>
            </div>
            {showModalUpdateOranizationSimOffers &&
                <ModalUpdateOrganizationSimOffers
                    isOpen={showModalUpdateOranizationSimOffers}
                    setIsOpen={setShowModalUpdateOranizationSimOffers}
                    organizationId={organizationId}
                    organizationSimOffers={data}
                    isLoading={isFetching}
                    setLaunchRefetch={setLaunchRefetch}
                />
            }
        </div>
    );
}

SimOffersPanel.propTypes = {
    organizationId: PropTypes.string.isRequired
};

export default SimOffersPanel;